// /src/data/books/FundametosBlockchain/data/cap10.js
const CAP10 = {
  id: "fundamentos-blockchain-10",
  slug: "componentes-tecnicos-nft",
  title: "Componentes Técnicos",
  sections: [
    {
      id: "hero",
      heading: "Componentes Técnicos de un Esquema NFT",
      text: `En esta sección reunimos los bloques técnicos que hacen posible un sistema NFT operativo de extremo a extremo.
Partimos de la cadena de bloques y el consenso, seguimos con los contratos inteligentes y el modelo de estados,
y cerramos con direcciones, transacciones, carteras y estándares clave para emisión, transferencia y metadatos.`
    },

    /* =========================
       1) BLOCKCHAIN Y CONSENSO
       ========================= */
    {
      id: "blockchain-y-consenso",
      heading: "Blockchain y consenso",
      text: `Una blockchain es una base de datos distribuida y encadenada por hash que almacena registros inmutables.
En redes abiertas, el consenso resuelve el “problema bizantino”: los nodos acuerdan el mismo historial sin confiar entre sí.
Una vez confirmado un bloque por la mayoría económica o por los validadores, revertirlo es impráctico.`
    },
    {
      id: "mecanismos-consenso",
      heading: "Mecanismos de consenso (visión general)",
      list: [
        {
          title: "Prueba de Trabajo (PoW)",
          text: "Seguridad basada en cómputo; los mineros compiten resolviendo acertijos criptográficos para proponer bloques."
        },
        {
          title: "Prueba de Participación (PoS)",
          text: "Validadores seleccionados por stake y reglas de protocolo; reduce consumo energético y acelera la finalidad."
        },
        {
          title: "Finalidad y reorganizaciones",
          text: "Bloques se consideran ‘finales’ tras umbrales/epochs; reorgs profundos son raros en redes maduras."
        }
      ]
    },

    /* =========================
       2) PLATAFORMAS
       ========================= */
    {
      id: "plataformas-nft",
      heading: "Plataformas comunes para NFT",
      text: `El ecosistema NFT se implementa principalmente sobre plataformas con soporte robusto de contratos inteligentes.
Ethereum es la referencia; también existen soluciones especializadas y enterprise.`,
      list: [
        { title: "Ethereum (EVM)", text: "Entorno seguro para contratos inteligentes, amplia compatibilidad con monederos y estándares NFT." },
        { title: "Flow", text: "Diseñada para experiencias de coleccionables de gran escala; lenguaje Cadence con recursos nativos." },
        { title: "EOS/Antelope", text: "Bajo tiempo de bloque y alto rendimiento para casos específicos." },
        { title: "Hyperledger (permisionadas)", text: "Redes privadas orientadas a consorcios empresariales con reglas de acceso." },
        { title: "Otras", text: "Sidechains y L2 EVM para costos menores y mayor throughput según el caso de uso." }
      ]
    },

    /* =========================
       3) CONTRATOS INTELIGENTES
       ========================= */
    {
      id: "contratos-inteligentes",
      heading: "Contratos inteligentes y ejecución",
      text: `Los contratos inteligentes permiten automatizar la lógica de negocio en un entorno distribuido.
Son programas desplegados en la cadena que ejecutan transiciones de estado de forma determinista al recibir mensajes/llamadas.
Al compartir reglas y estados entre nodos, se garantiza transparencia, trazabilidad y coherencia global.`
    },
    {
      id: "modelo-estado",
      heading: "Modelo de estado y transiciones",
      list: [
        {
          title: "Estado global compartido",
          text: "El contrato mantiene variables (propietarios, saldos, URIs) replicadas en todos los nodos."
        },
        {
          title: "Transiciones deterministas",
          text: "Cada llamada válida ejecuta funciones que modifican el estado de acuerdo con reglas predefinidas."
        },
        {
          title: "Eventos (logs)",
          text: "Se emiten eventos para notificar transferencias y cambios, facilitando indexación por monederos y dApps."
        }
      ]
    },

    /* =========================
       4) DIRECCIONES Y TRANSACCIONES
       ========================= */
    {
      id: "direcciones-transacciones",
      heading: "Direcciones, transacciones y firmas",
      text: `Una dirección de blockchain identifica al remitente/destinatario de activos. Se deriva de un par de claves (pública/privada).
Para transferir un NFT, el titular firma digitalmente la transacción, que invoca funciones del contrato.
Los monederos simplifican este flujo y protegen la clave privada del usuario.`
    },

    /* =========================
       5) ESTÁNDARES NFT Y UTILIDADES RELACIONADAS
       ========================= */
    {
      id: "estandares-nft",
      heading: "Estándares técnicos de referencia para NFT",
      text: `Los estándares definen interfaces mínimas para interoperabilidad entre contratos, monederos y mercados.
Aseguran que una dApp pueda reconocer, transferir y mostrar un NFT correctamente.`,
      list: [
        {
          title: "ERC-721 (NFT único)",
          text: "Interfaz base de NFT no fungibles: ownerOf, balanceOf, safeTransferFrom, approve, setApprovalForAll, tokenURI, y eventos Transfer/Approval."
        },
        {
          title: "ERC-1155 (Multi-Token)",
          text: "Permite manejar fungibles y no fungibles en un solo contrato; transferencias en lote y menor consumo de gas."
        },
        {
          title: "ERC-2981 (Regalías)",
          text: "royaltyInfo para estandarizar porcentajes y destinatarios de regalías a nivel de colección o token."
        },
        {
          title: "ERC-4907 (Alquiler/cesión)",
          text: "Rol de usuario con expiración; separa propietario de usuario temporal sin transferir la propiedad."
        },
        {
          title: "ERC-5192 (Soulbound minimalista)",
          text: "Define tokens bloqueados (no transferibles) para credenciales o reputación."
        },
        {
          title: "ERC-6551 (Cuentas ligadas a NFT)",
          text: "El NFT actúa como cuenta/cartera y puede poseer activos o ejecutar acciones en nombre del token."
        },
        {
          title: "ERC-777 (Fungible avanzado, apoyo a flujos NFT)",
          text: "Tokens fungibles con operadores y hooks; útil en mercados/puentes que interactúan con NFTs y pagos."
        }
      ]
    },

    /* =========================
       6) METADATOS, ALMACENAMIENTO Y CONTENIDO
       ========================= */
    {
      id: "metadatos-y-almacenamiento",
      heading: "Metadatos, almacenamiento y contenido",
      text: `El valor percibido del NFT depende de su metadato y del contenido asociado.
La práctica habitual es separar el identificador on-chain del contenido off-chain verificable.`,
      list: [
        {
          title: "Metadata JSON",
          text: "Estructuras con nombre, descripción, imagen, atributos. Se acceden vía tokenURI y deben ser verificables."
        },
        {
          title: "IPFS/Arweave",
          text: "Almacenamiento distribuido para la imagen/asset y el JSON de metadatos, evitando enlaces frágiles centralizados."
        },
        {
          title: "Inmutabilidad práctica",
          text: "Fijar CIDs o políticas de actualización claras; si hay mutabilidad, registrar versiones y reglas transparentes."
        }
      ]
    },

    /* =========================
       7) SEGURIDAD Y BUENAS PRÁCTICAS
       ========================= */
    {
      id: "seguridad-buenas-practicas",
      heading: "Seguridad y buenas prácticas",
      list: [
        {
          title: "Mínima superficie y librerías auditadas",
          text: "Usar implementaciones probadas; evitar lógica innecesaria en funciones críticas (mint/transfer/burn)."
        },
        {
          title: "Validaciones estrictas",
          text: "Revisar ownership, approvals y receptores (safeTransfer) para prevenir pérdidas o bloqueos de tokens."
        },
        {
          title: "Eventos consistentes",
          text: "Emitir eventos estándar en cada transición para facilitar indexación, analítica y conciliación."
        },
        {
          title: "Gestión de permisos",
          text: "Roles claros para acuñación/quema/pausa; usar multisig y time-locks cuando aplique."
        },
        {
          title: "Pruebas y auditoría",
          text: "Cobertura de pruebas, análisis estático, fuzzing y auditorías externas antes del despliegue final."
        }
      ]
    },

    /* =========================
       8) FLUJO TÉCNICO RESUMIDO (NFT)
       ========================= */
    {
      id: "flujo-tecnico",
      heading: "Flujo técnico resumido de un NFT",
      list: [
        { title: "1) Diseño del contrato", text: "Elegir estándar (ERC-721/1155), definir supply, permisos y metadatos." },
        { title: "2) Almacenamiento de contenido", text: "Publicar arte/JSON en IPFS/Arweave; fijar CID o política de actualización." },
        { title: "3) Despliegue y verificación", text: "Compilar, desplegar, verificar bytecode y publicar ABI para transparencia." },
        { title: "4) Acuñación (mint)", text: "Emitir tokens según política (pública, allowlist, reclamos) y registrar eventos." },
        { title: "5) Transferencia segura", text: "safeTransferFrom/transferencias en lote (1155); validar receptores y approvals." },
        { title: "6) Integración de mercado", text: "RoyaltyInfo (2981), subastas/ventas, alquiler (4907) o cuentas ligadas (6551) si aplica." }
      ]
    },

    /* =========================
       9) CONCLUSIÓN
       ========================= */
    {
      id: "conclusion",
      heading: "Conclusión",
      text: `Un stack NFT funcional combina blockchain y consenso para inmutabilidad, contratos inteligentes para lógica y estado,
y estándares de token/metadata para interoperabilidad. Direcciones, firmas y transacciones cierran el ciclo operativo
con seguridad y trazabilidad. Con estas bases técnicas, una dApp puede emitir, transferir, mostrar y extender NFTs
de forma predecible y compatible con el ecosistema.`
    }
  ]
};

export default CAP10;
